import { Button, Grid, Hidden, Paper, Tab, Tabs, Tooltip, Typography } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { withStyles } from '@material-ui/core/styles';
import ScreenRotationIcon from '@material-ui/icons/ScreenRotation';
import { mdiCardAccountDetailsOutline } from '@mdi/js';
import Icon from '@mdi/react';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';

import { App, Primaccess } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import { Notice } from '_components/elements';

/* */
import i18n from '_utils/i18n';
import masterClient from '_utils/masterClient';
import { getAuthorization, isFeatureEnabled } from '_utils/security';

/* */
import BadgesList from './BadgesList';
import ChooseProduct from './ChooseProduct';
import SearchMoves from './SearchMoves';
import SearchOperations from './SearchOperations';

/* */
const styles = {
  balancePaper: {
    padding: 16,
    marginBottom: 12,
  },
};

const ROWS_PER_PAGE = 16;

/* */
class PrimaccessView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      // Screen
      error: null,
      loading: true,
      tabIndex: 0,
      modal: false,
      movesTabFocus: false,
      // Data
      client: null,
      balance: null,
    };

    const golfFeatures = props.golf?.options?.features || {};
    this.permissions = {
      nfcPass: isFeatureEnabled(golfFeatures, 'primaccess/nfcPass'),
      autoRefill: isFeatureEnabled(golfFeatures, 'primaccess/autoRefill'),
      autoEnroll: isFeatureEnabled(golfFeatures, 'primaccess/autoEnroll'),
    };
  }

  /* */
  componentDidMount() {
    this.load();
  }

  /* */
  load = async () => {
    const { golf, account } = this.props;
    const match = App.functions.match(account, golf);

    if (!match) {
      this.setState({ loading: false });
      return;
    }

    const params = { golfId: golf.id, accountId: account.id };

    try {
      let client = await Primaccess.api.getClient(masterClient, params);

      // On crée le client s'il n'existe pas et l'auto-enroll activé dans les options
      // puis on recupère les infos de nouveau
      if (!client && this.permissions.autoEnroll) {
        await Primaccess.api.createClient(masterClient, params);
        client = await Primaccess.api.getClient(masterClient, params);
      }

      if (client) {
        // On isole le solde qui doit être rafraichit indépendamment
        const { balance, ...clientInfo } = client;
        this.setState({ client: clientInfo, balance });
        this.props.update({ clientId: clientInfo.id });
      }
    } catch (error) {
      this.setState({ error });
    } finally {
      this.setState({ loading: false });
    }
  };

  /* */
  isAuthorized(path) {
    const { golf, account } = this.props;
    const { isAuthorized } = getAuthorization(path, { golf, account });

    return isAuthorized;
  }

  /* */
  handleSwitchModal = () => {
    this.setState((prevState) => ({ modal: !prevState.modal }));
  };

  /* */
  handleTabChange = (e, tabIndex) => {
    const newState = { tabIndex };
    if (tabIndex === 1) {
      newState.movesTabFocus = true;
    }
    this.setState(newState);
  };

  /* */
  refreshBalance = (balance) => this.setState({ balance });

  /* */
  renderTabs() {
    const { tabIndex, movesTabFocus } = this.state;

    const movesTabDisplay = tabIndex === 1 ? 'block' : 'none';
    const operationsTabDisplay = tabIndex === 0 ? 'block' : 'none';

    return (
      <Grid item md={8} sm={12} xs={10}>
        <Tabs centered value={tabIndex} textColor='primary' indicatorColor='primary' onChange={this.handleTabChange}>
          <Tab label='Transactions' />
          <Tab label='Passages' />
        </Tabs>
        <br />
        <Hidden xsDown implementation='css'>
          <Box style={{ display: operationsTabDisplay }}>
            <SearchOperations rowsPerPage={ROWS_PER_PAGE} onRefresh={this.refreshBalance} />
          </Box>
          <Box style={{ display: movesTabDisplay }}>
            <SearchMoves isFocused={movesTabFocus} rowsPerPage={ROWS_PER_PAGE} />
          </Box>
        </Hidden>
        <Hidden smUp implementation='css'>
          <Box display='flex' alignItems='center' flexDirection='column'>
            <Typography variant='caption'>
              {'Pour une lisibilité optimale, veuillez basculer votre écran en mode paysage.'}
            </Typography>
            <br />
            <ScreenRotationIcon fontSize='large' />
          </Box>
        </Hidden>
      </Grid>
    );
  }

  /* */
  renderBalance() {
    const { balance } = this.state;
    const { classes, history, account, golf } = this.props;

    if (balance === null) return null;

    const primaccessPaymentMethods = App.functions.getPaymentMethods(account, golf, 'primaccess');
    const isOnlineRechargeDisabled = primaccessPaymentMethods.length === 1 && primaccessPaymentMethods[0] === 1;

    return (
      <Fragment>
        <Typography gutterBottom variant='h6' component='h2'>
          {'Mon solde'}
        </Typography>
        <Paper className={classes.balancePaper}>
          <Typography variant='h4' align='center' component='span'>
            {i18n.l('number', balance)}
          </Typography>
        </Paper>
        <Tooltip
          title={isOnlineRechargeDisabled ? 'La recharge en ligne est désactivée. Veuillez passer à l\'accueil du golf.' : ''}
          placement='top'
        >
          <span style={{ width: '100%' }}>
            <Button
              fullWidth
              color='secondary'
              variant='contained'
              onClick={this.handleSwitchModal}
              disabled={isOnlineRechargeDisabled}
            >
              {'Recharger'}
            </Button>
          </span>
        </Tooltip>
        {this.permissions.autoRefill && (
          <Tooltip
            title={isOnlineRechargeDisabled ? 'La recharge automatique en ligne est désactivée. Veuillez passer à l\'accueil du golf.' : ''}
            placement='top'
          >
            <span style={{ width: '100%', marginTop: 12, display: 'inline-block' }}>
              <Button
                fullWidth
                color='secondary'
                variant='outlined'
                onClick={() => history.push('/primaccess/autorefill')}
                disabled={isOnlineRechargeDisabled}
              >
                {'Rechargement automatique'}
              </Button>
            </span>
          </Tooltip>
        )}
        <br />
      </Fragment>
    );
  }

  /* */
  renderBadges() {
    const { client } = this.state;

    return <BadgesList client={client} permissions={this.permissions} onError={(error) => this.setState({ error })} />;
  }

  /* */
  renderClientData() {
    const { error, client } = this.state;

    if (!client && error) return null;

    if (!client && !error) {
      return (
        <Box display='flex' alignItems='center' flexDirection='column'>
          <Icon path={mdiCardAccountDetailsOutline} size={6} />
          <Typography variant='subtitle1'>{"Vous n'utilisez pas ce service actuellement."}</Typography>
          <Typography variant='subtitle1'>{'Veuillez vous rapprocher de votre golf.'}</Typography>
        </Box>
      );
    }

    return (
      <>
        <Notice context='PRACTICE' />
        <Grid container justify='center' spacing={40}>
          <Grid item md={4} sm={8} xs={10}>
            {this.renderBalance()}
            {this.renderBadges()}
          </Grid>
          {this.renderTabs()}
        </Grid>
      </>
    );
  }

  /* */
  renderProductsModal() {
    const { client, modal } = this.state;
    const { history } = this.props; // follow router-dom

    if (!client) return null;

    return <ChooseProduct history={history} open={modal} handleSwitchModal={this.handleSwitchModal} />;
  }

  /* */
  render() {
    const { error, loading } = this.state;

    return (
      <Screen error={error} loading={loading} title='Practice'>
        {this.renderClientData()}
        {this.renderProductsModal()}
      </Screen>
    );
  }
}

const mapStateToProps = ({ app: { golf, account }, primaccess: { clientId } }) => ({ golf, account, clientId });

const StyledComponent = withStyles(styles)(PrimaccessView);

export default connect(mapStateToProps, Primaccess.actions)(StyledComponent);
